import { useState, useEffect } from 'react';
import { Draggable, Droppable, DropResult, DragDropContext } from '@hello-pangea/dnd';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';

import { useTranslate } from 'src/locales';

import Iconify from 'src/components/iconify';
import CustomPopover, { usePopover } from 'src/components/custom-popover';

import { PickerColumn } from './data-list.types';

// ----------------------------------------------------------------------

interface DataListColumnPickerProps {
  /** Full ordered model (fixed + non-fixed) from buildPickerModel. */
  model: PickerColumn[];
  onSave: (model: PickerColumn[]) => void;
  /** Cap on how many columns can be visible at once; further check-ons are blocked (e.g. summary line). */
  maxVisible?: number;
  /** Trigger-button label; defaults to "Columns". */
  buttonLabel?: string;
}

/**
 * "Columns" control for the shared list (JT1-1528): a popover that toggles column visibility and
 * drag-reorders columns, persisting on Apply. Fixed columns are shown locked and can't be dragged or
 * hidden. Cloned from export-field-mapper's @hello-pangea/dnd usage — the repo's DnD pattern.
 *
 * Edits are staged locally and only saved on Apply, so a mis-drag doesn't immediately repaint the
 * live list; Cancel/close reverts to the persisted layout.
 */
export default function DataListColumnPicker({ model, onSave, maxVisible, buttonLabel }: DataListColumnPickerProps) {
  const { t } = useTranslate();
  const popover = usePopover();

  const [draft, setDraft] = useState<PickerColumn[]>(model);

  // Re-seed the draft whenever the popover opens or the persisted model changes.
  useEffect(() => {
    if (popover.open) setDraft(model);
  }, [popover.open, model]);

  const fixed = draft.filter((c) => c.fixed);
  const reorderable = draft.filter((c) => !c.fixed);

  // When capped, hidden columns can't be turned on once the cap is reached (fixed columns count too).
  const atMax = maxVisible !== undefined && draft.filter((c) => c.visible).length >= maxVisible;

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const next = Array.from(reorderable);
    const [moved] = next.splice(result.source.index, 1);
    next.splice(result.destination.index, 0, moved);
    setDraft([...fixed, ...next]);
  };

  const toggle = (key: string) => {
    setDraft((prev) => {
      const visibleCount = prev.filter((c) => c.visible).length;
      return prev.map((c) => {
        if (c.key !== key) return c;
        // Block turning a column on once the cap is reached; turning off is always allowed.
        if (!c.visible && maxVisible !== undefined && visibleCount >= maxVisible) return c;
        return { ...c, visible: !c.visible };
      });
    });
  };

  const apply = () => {
    onSave(draft);
    popover.onClose();
  };

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<Iconify icon="solar:settings-bold" />}
        onClick={popover.onOpen}
      >
        {buttonLabel ?? t('common.columns', { defaultValue: 'Columns' })}
      </Button>

      <CustomPopover open={popover.open} onClose={popover.onClose} sx={{ width: 280, p: 1.5 }}>
        <Typography variant="subtitle2" sx={{ px: 1, pb: maxVisible !== undefined ? 0.25 : 1 }}>
          {buttonLabel ?? t('common.columns', { defaultValue: 'Columns' })}
        </Typography>
        {maxVisible !== undefined && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 1, pb: 1 }}>
            {t('common.select_up_to', { count: maxVisible, defaultValue: `Select up to ${maxVisible}` })}
          </Typography>
        )}

        {fixed.map((col) => (
          <Stack key={col.key} direction="row" alignItems="center" sx={{ px: 1, py: 0.5, opacity: 0.7 }}>
            <Box sx={{ width: 40, display: 'flex', justifyContent: 'center' }}>
              <Iconify icon="solar:lock-keyhole-minimalistic-bold" width={16} />
            </Box>
            <Checkbox checked disabled size="small" />
            <Typography variant="body2">{col.label}</Typography>
          </Stack>
        ))}

        {fixed.length > 0 && reorderable.length > 0 && <Divider sx={{ my: 0.5 }} />}

        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="data-list-columns">
            {(provided) => (
              <Box ref={provided.innerRef} {...provided.droppableProps}>
                {reorderable.map((col, index) => (
                  <Draggable key={col.key} draggableId={col.key} index={index}>
                    {(dragProvided) => (
                      <Stack
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        direction="row"
                        alignItems="center"
                        sx={{ px: 1, py: 0.5 }}
                      >
                        <Box {...dragProvided.dragHandleProps} sx={{ width: 40, display: 'flex', justifyContent: 'center', cursor: 'grab' }}>
                          <Iconify icon="solar:hamburger-menu-linear" width={18} />
                        </Box>
                        <Checkbox
                          checked={col.visible}
                          disabled={!col.visible && atMax}
                          size="small"
                          onChange={() => toggle(col.key)}
                        />
                        <Typography variant="body2">{col.label}</Typography>
                      </Stack>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        </DragDropContext>

        <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ pt: 1, px: 1 }}>
          <Button size="small" color="inherit" onClick={popover.onClose}>
            {t('common.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button size="small" variant="contained" onClick={apply}>
            {t('common.apply', { defaultValue: 'Apply' })}
          </Button>
        </Stack>
      </CustomPopover>
    </>
  );
}
